import React, { useMemo, useRef, useEffect } from 'react';
import PrintableTileWrapper from '@/app/features/reports/PrintableTileWrapper';
import SdaPrintColumnHeadings from '@/app/features/reports/components/sda-print-column-headings';
import { useReportViewContext } from '@/app/features/reports/components/report-viewer-context';
import { FlattenedItem, ReportItem } from '@/app/features/reports/utils/entities';
import { useFlattenedItems } from '@/app/features/reports/utils/flattenData';
import { computePageBreakIndices, findOversizeIndices, SDA_PRINT_PAGE_BUDGET_PX } from '@/app/features/reports/utils/sdaPrintPagination';
import { Assessment } from '@app.raytd.com/store';
import { TestSuite } from 'store/src/lib/tests/entity';

// raytd/web#157: Chromium's printToPDF does not honour `break-inside: avoid` for an SDA tile
// that starts too close to a page's bottom to fit whole, regardless of DOM structure (measured
// exhaustively in raytd/web#153 — CSS Grid, a real rowSpan table, a two-row table, a single-row
// table, every break-inside/break-after/page-break-* combination, all against the real 28-item
// report via tools/print-pagination-harness — zero effect on the total split count, only on
// which item split). Rather than asking Chromium's fragmentation engine to decide, this measures
// each tile's real rendered height under an actual print pass and forces an explicit break (the
// existing, previously-unused `.page-break` class — `break-before: page`, print.css) before any
// tile that won't fit in what's left of the "current" simulated page — see
// utils/sdaPrintPagination.ts for the pure decision logic this wraps.
//
// Injection point — verified empirically against tools/print-pagination-harness's real
// Playwright/CDP printToPDF call, not trusted on documented behaviour alone (this project's
// whole reason for #157 existing is a claim exactly like that turning out false for
// `break-inside`). Two things were checked, and they gave DIFFERENT answers:
//   1. Does a `beforeprint` DOM mutation reach the rendered PDF at all? Yes — confirmed with a
//      throwaway marker element.
//   2. Is the DOM already laid out under `@media print` (this container sits under
//      `.hidden.print:block` in ReportView.tsx, so it's `display: none` outside print) by the
//      time that handler runs, so a height measurement means anything? NO — logging
//      `window.matchMedia('print').matches` and this container's own `getBoundingClientRect()`
//      inside a `beforeprint` handler showed `matches: false` and a 0×0 box: Chromium's
//      CDP-triggered printToPDF fires `beforeprint` BEFORE it switches the document to print
//      media, not after (unlike the common `window.print()` mental model). Measuring there
//      would silently inject breaks against a garbage all-zero height list.
// `window.matchMedia('print')`'s own `change` event is the mechanism that actually lines up:
// logging inside a `change` listener showed `matches: true` with the container already
// `display: table-cell` at its real ~1034px print-layout width and each tile's real rendered
// height. Breaks are injected on the `matches: true` transition and stripped again on the
// `matches: false` transition (print media deactivating), so the screen view — and a second
// print pass after a re-render — never accumulates stale break nodes measured against a
// since-changed layout. This is a platform-level media-query event, not something raytd-api's
// ChromePdfRenderer job needs to trigger differently — any Chromium printToPDF call switches
// media the same way, so no cross-repo change is needed for this piece.
//
// CORRECTION (raytd/web#195, 2026-09-20) — the paragraph above is only half true and its last
// sentence is WRONG. The injection lands, and the splits go away, but when the `change` event is
// fired by Chromium's own print pass it runs AFTER the page count has been fixed, so the content the
// breaks push down is silently dropped: a real render lost 11 of 28 tiles plus the Reference Image
// Appendix (which prints last) with `splits: []` — the harness only counted splits, so nothing flagged
// it. The breaks must already be in the DOM before Chromium paginates. raytd-api's ChromePdfRenderer
// therefore emulates print media (at the paper's printable-area viewport) BEFORE printing for SDA
// (`emulatePrintMedia`, raytd/api#147), which fires this handler while the page is live; the harness
// mirrors that and now fails on an incomplete PDF (tools/print-pagination-harness, "Completeness").
// Browser Ctrl+P is not a supported print path, so it isn't covered.
const SDA_PAGE_BREAK_CLASS = 'page-break';
const INJECTED_BREAK_ATTR = 'data-sda-injected-page-break';
// Marks a tile taller than a page so print.css lets it fragment (its `break-inside: avoid` can never
// be satisfied; Chromium pushes it a whole page down and leaves a blank one behind).
const OVERSIZE_TILE_ATTR = 'data-sda-oversize';

function removeInjectedPageBreaks(container: HTMLElement) {
    container.querySelectorAll(`[${INJECTED_BREAK_ATTR}]`).forEach(node => node.remove());
    container.querySelectorAll(`[${OVERSIZE_TILE_ATTR}]`).forEach(node => node.removeAttribute(OVERSIZE_TILE_ATTR));
}

const collectGroupIds = (items: ReportItem[], accumulator: Set<string>) => {
    items.forEach(item => {
        accumulator.add(item.id);
        if (Array.isArray(item.children) && item.children.length > 0) collectGroupIds(item.children, accumulator);
        if (Array.isArray(item.content) && item.content.length > 0) accumulator.add(item.id);
    });
    return accumulator;
};

const PrintableReportComponent: React.FC = () => {
    const { data, groupMode, report, isSdaFormat } = useReportViewContext();
    const sdaTilesContainerRef = useRef<HTMLDivElement>(null);

    // Measure-and-inject glue (raytd/web#157) — see the module-level comment above for why
    // `matchMedia('print')`'s `change` event, not `beforeprint`, is the injection point. Not
    // meaningfully unit-testable (jsdom doesn't lay out real print pagination —
    // getBoundingClientRect is a stub there), so this is verified via
    // tools/print-pagination-harness against a real report instead; the pure decision logic it
    // calls (computePageBreakIndices) IS unit tested, in isolation.
    useEffect(() => {
        if (!isSdaFormat) return undefined;
        const container = sdaTilesContainerRef.current;
        if (!container) return undefined;

        const injectPageBreaks = () => {
            // Idempotent: strip any breaks a previous pass injected before re-measuring, so a
            // second print pass can't compound stale breaks measured against an earlier,
            // possibly different layout.
            removeInjectedPageBreaks(container);

            const wrappers = Array.from(
                container.querySelectorAll<HTMLElement>(':scope > .printable-tile-wrapper')
            );
            if (wrappers.length === 0) return;

            // Plain getBoundingClientRect().height per wrapper would undercount: `.sda-print-
            // table-cell` (the container above) applies `print:space-y-2`, an 8px margin-top on
            // every wrapper but the first, which the height box doesn't include. Using each
            // wrapper's vertical span up to the NEXT wrapper's top (falling back to its own
            // height for the last one) folds that gap into the number naturally, without
            // hardcoding the gap value or needing to track whatever it resolves to — code-review
            // found this during raytd/web#157, before it ever reproduced as a visible split on
            // real content (found via reasoning about the CSS, not a harness re-run turning up
            // red).
            const rects = wrappers.map(wrapper => wrapper.getBoundingClientRect());
            const heights = rects.map((rect, index) =>
                index === rects.length - 1 ? rect.height : rects[index + 1].top - rect.top
            );
            // Every page carries the column-heading row (the hoisted `<thead>` only shows on page 1 —
            // see the comment on the injected copy below), and page 1 also carries the "Results"
            // title, so tiles get that much less room than the raw page height. Measured, not
            // hardcoded, so a heading restyle can't silently desync the budget.
            const printTable = container.closest('.sda-print-table');
            const headingsHeight =
                printTable?.querySelector<HTMLElement>('[data-testid="sda-print-column-headings"]')?.getBoundingClientRect().height ?? 0;
            const titleHeight =
                printTable?.querySelector<HTMLElement>('[data-testid="sda-print-results-title"]')?.getBoundingClientRect().height ?? 0;
            const pageBudget = SDA_PRINT_PAGE_BUDGET_PX - headingsHeight;
            const breakIndices = computePageBreakIndices(heights, pageBudget, pageBudget - titleHeight);
            const headingsSource = printTable?.querySelector<HTMLElement>('[data-testid="sda-print-column-headings"]');

            // QA 108.02 (raytd/web#147, item 1): a tile taller than a whole page (many photos, now
            // uncapped) is split by Chromium itself, and the copy above only heads the page a break
            // STARTS — the continuation page had no column headings. Such a tile gets the heading
            // <thead> inside its own table instead, which Chromium repeats on every fragment of that
            // table (including the first, so it also replaces the standalone copy below).
            const oversizeIndices = new Set(findOversizeIndices(heights, pageBudget));
            const headingsHead = headingsSource?.querySelector('thead');
            oversizeIndices.forEach(index => {
                wrappers[index].setAttribute(OVERSIZE_TILE_ATTR, 'true');
                const tileTable = wrappers[index].querySelector<HTMLElement>('.sda-tile-content-row');
                const tileBody = tileTable?.querySelector('tbody');
                if (!headingsHead || !tileTable || !tileBody) return;
                const repeatingHead = headingsHead.cloneNode(true) as HTMLElement;
                repeatingHead.setAttribute(INJECTED_BREAK_ATTR, 'true');
                tileTable.insertBefore(repeatingHead, tileBody);
            });

            breakIndices.forEach(index => {
                // An oversize tile carries its own break-before (print.css) instead of a separate
                // empty break node: a content-less break div ahead of a tile that can't fit the
                // page left a whole blank page behind (measured, QA 108.02).
                if (oversizeIndices.has(index)) {
                    wrappers[index].setAttribute(OVERSIZE_TILE_ATTR, 'break');
                    return;
                }
                const breakNode = document.createElement('div');
                breakNode.className = SDA_PAGE_BREAK_CLASS;
                breakNode.setAttribute(INJECTED_BREAK_ATTR, 'true');
                // QA 108.02 item 1: Chromium doesn't repeat the `display: table-header-group` row
                // across these forced breaks (measured on a real 15-page render: headings on page 1
                // only), so each page an injected break starts gets its own copy. Same node the
                // hoisted row renders — one source of truth for labels/widths — minus its testid.
                if (headingsSource) {
                    const copy = headingsSource.cloneNode(true) as HTMLElement;
                    copy.removeAttribute('data-testid');
                    breakNode.appendChild(copy);
                }
                wrappers[index].parentElement?.insertBefore(breakNode, wrappers[index]);
            });
        };

        const cleanUpPageBreaks = () => removeInjectedPageBreaks(container);

        const printMediaQuery = window.matchMedia('print');
        const handlePrintMediaChange = (event: MediaQueryListEvent) => {
            if (event.matches) {
                injectPageBreaks();
            } else {
                cleanUpPageBreaks();
            }
        };
        printMediaQuery.addEventListener('change', handlePrintMediaChange);
        // Cover the (already-in-print-media) case where this effect attaches after the
        // transition already happened, rather than relying solely on catching the edge.
        if (printMediaQuery.matches) injectPageBreaks();

        return () => {
            printMediaQuery.removeEventListener('change', handlePrintMediaChange);
            cleanUpPageBreaks();
        };
        // Re-registered whenever the report's underlying data changes so a stale closure never
        // measures against a since-replaced tile set; `isSdaFormat` gates the whole effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSdaFormat, data]);

    const expandedForPrint = useMemo(() => collectGroupIds(data ?? [], new Set<string>()), [data]);

    const flattenedItems = useFlattenedItems(data ?? [], expandedForPrint, groupMode);

    const headerMap = useMemo(() => {
        const map = new Map<string, FlattenedItem>();
        flattenedItems.forEach(item => {
            if (!item.content) map.set(item.id, item);
        });
        return map;
    }, [flattenedItems]);

    const printableItems = useMemo(
        () => flattenedItems.filter(item => item.content && item.testCase),
        [flattenedItems]
    );

    const assessmentsById = useMemo(() => {
        const map = new Map<number, Assessment>();
        (report?.assessments ?? []).forEach(assessment => map.set(assessment.id, assessment));
        return map;
    }, [report?.assessments]);

    const testSuitesById = useMemo(() => {
        const map = new Map<number, TestSuite>();
        (report?.testSuites ?? []).forEach(testSuite => map.set(testSuite.id, testSuite));
        return map;
    }, [report?.testSuites]);

    if (!printableItems.length) return null;

    let previousGroupHeaderId: string | null = null;

    const tiles = printableItems.map(item => {
        const testCase = item.testCase;
        if (!testCase) return null;
        const assessment = assessmentsById.get(testCase.assessmentId);
        const testSuite = testSuitesById.get(testCase.testSpecification?.testSuiteId ?? -1);

        // SDA tiles now flow multiple-per-page (PrintableTileWrapper drops the forced
        // page break for isSdaFormat) — every non-SDA format still gets one tile per
        // page, where the same heading repeating above each tile reads as an intentional
        // running header. Once several SDA tiles from the same group share a page, that
        // same repetition instead reads as a stack of duplicate headings, so only the
        // first tile of a run of consecutive same-group tiles renders it.
        const isRepeatedGroupHeader = isSdaFormat && item.groupHeaderId === previousGroupHeaderId;
        previousGroupHeaderId = item.groupHeaderId ?? null;
        const header = !isRepeatedGroupHeader && item.groupHeaderId
            ? headerMap.get(item.groupHeaderId)
            : undefined;
        // RepeatingGroupHeader derives its h2 title from `parents` first, falling back to
        // `header` only when parents is empty — so suppressing just `header` above isn't
        // enough on its own; `parents` (the same non-empty breadcrumb for every item in
        // the group) still lands as the title and the heading kept repeating anyway.
        const parents = isRepeatedGroupHeader ? [] : (item.parents ?? []);

        return (
            <PrintableTileWrapper
                key={item.id}
                item={testCase}
                parents={parents}
                header={header}
                assessment={assessment}
                testSuite={testSuite}
                isSdaFormat={isSdaFormat}
            />
        );
    });

    if (!isSdaFormat) {
        return (
            <div className="printable-report-container space-y-6" data-testid="printable-report">
                {tiles}
            </div>
        );
    }

    // SDA's Clause/Requirement/Result/Observations/Recommendations column heading used to be
    // baked into every tile in print output (test-tile.tsx's per-tile TextTile titles) — QA
    // asked for it once per page instead (raytd/web QA 108.02, item 2). A single hoisted DOM
    // node above the tile list would only repeat on page 1; Chrome's printToPDF (like other
    // browsers) only repeats content across page boundaries for `display: table-header-group`
    // inside a `display: table` ancestor — the standard CSS mechanism for a paginated running
    // header, not sticky positioning (print.css already force-disables `position: sticky`
    // globally, and sticky doesn't repeat across page breaks under pagination anyway).
    // The body's own tiles are NOT turned into table rows themselves — CSS auto-generates an
    // anonymous table-row wrapping EACH direct child of a table-row-group, which would lay
    // sibling tiles out side-by-side instead of stacked. Wrapping the whole tile list in one
    // `table-row`/`table-cell` pair keeps it as ordinary block flow (and keeps `break-inside:
    // avoid`/`break-after-page`-less SDA pagination working exactly as before) while still
    // satisfying the "single cell" table structure the header needs to repeat against.
    return (
        <div className="printable-report-container-sda sda-print-table" data-testid="printable-report">
            {/* QA 108.02 item 3: section title on the first page. A plain block (padding, not
                margin, so its measured height is its real footprint) above the table head, so it
                prints once. */}
            <div data-testid="sda-print-results-title" className="px-4 print:px-2 pb-2 text-lg font-semibold">
                Results
            </div>
            <div className="sda-print-table-head" data-testid="sda-print-table-head">
                <div className="sda-print-table-row">
                    <div className="sda-print-table-cell">
                        <SdaPrintColumnHeadings />
                    </div>
                </div>
            </div>
            <div className="sda-print-table-body">
                <div className="sda-print-table-row">
                    <div className="sda-print-table-cell print:space-y-2" ref={sdaTilesContainerRef}>
                        {tiles}
                    </div>
                </div>
            </div>
        </div>
    );
};

const PrintableReport = React.memo(PrintableReportComponent);

export default PrintableReport;

